import {
  Button,
  Checkbox,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useState } from "react";
import { ConfirmActionModal } from "@how/ui";
import {
  DAY_LABEL,
  JOINING_LABEL,
  VISIBILITY_LABEL,
  useCampusOptions,
  useGroupCategories,
  useSaveGroup,
  useSetGroupActive,
  type GroupFields,
  type GroupJoiningRule,
  type GroupRow,
  type GroupVisibility,
  type Weekday,
} from "./group-queries";

const DAYS = (Object.keys(DAY_LABEL) as Weekday[]).map((value) => ({
  value,
  label: DAY_LABEL[value],
}));
const VISIBILITIES = (Object.keys(VISIBILITY_LABEL) as GroupVisibility[]).map((value) => ({
  value,
  label: VISIBILITY_LABEL[value],
}));
const JOINING = (Object.keys(JOINING_LABEL) as GroupJoiningRule[]).map((value) => ({
  value,
  label: JOINING_LABEL[value],
}));

const EMPTY: GroupFields = {
  name: "",
  slug: "",
  description: "",
  categoryId: null,
  campusId: null,
  meetingDay: null,
  meetingTime: "",
  meetingLocation: "",
  visibility: "public",
  joiningRule: "request",
  capacity: null,
  ageMin: null,
  ageMax: null,
  membersConfidential: false,
};

export interface GroupModalProps {
  /** null to add a group, a row to edit it; undefined when closed. */
  readonly group: GroupRow | null | undefined;
  /** groups.manage: may add, archive and restore. */
  readonly canManage: boolean;
  /** May edit this group: groups.manage, or groups.manage_assigned for a group the caller leads. */
  readonly canEdit?: boolean;
  readonly onClose: () => void;
  readonly onCreated?: (groupId: string) => void;
}

/**
 * One dialog for adding and editing a group. The description is written knowing a member reads it in the finder
 * and a visitor may read it on the website; visibility and the joining rule are what the member's half obeys.
 */
export function GroupModal({ group, canManage, canEdit, onClose, onCreated }: GroupModalProps) {
  const save = useSaveGroup();
  const setActive = useSetGroupActive();
  const categories = useGroupCategories();
  const campuses = useCampusOptions();
  const editable = group === null ? canManage : (canEdit ?? canManage);
  const [fields, setFields] = useState<GroupFields>(
    group === null || group === undefined
      ? EMPTY
      : {
          name: group.name,
          slug: group.slug,
          description: group.description ?? "",
          categoryId: group.categoryId,
          campusId: group.campusId,
          meetingDay: group.meetingDay,
          meetingTime: group.meetingTime === null ? "" : group.meetingTime.slice(0, 5),
          meetingLocation: group.meetingLocation ?? "",
          visibility: group.visibility,
          joiningRule: group.joiningRule,
          capacity: group.capacity,
          ageMin: group.ageMin,
          ageMax: group.ageMax,
          membersConfidential: group.membersConfidential,
        },
  );
  const [confirming, setConfirming] = useState(false);
  const text =
    (key: "name" | "slug" | "description" | "meetingTime" | "meetingLocation") =>
    (event: { currentTarget: { value: string } }) => {
      const value = event.currentTarget.value;
      setFields((current) => ({ ...current, [key]: value }));
    };
  const number = (key: "capacity" | "ageMin" | "ageMax") => (value: string | number) => {
    setFields((current) => ({ ...current, [key]: typeof value === "number" ? value : null }));
  };

  const code = (save.error as { code?: string } | null)?.code;
  const errorText =
    code === "23505"
      ? "Another group already uses that name or address."
      : code === "23514"
        ? "Check the ages: the youngest cannot be older than the oldest."
        : code === "P0002"
          ? "The category or campus is not one of this church's. Choose again."
          : save.isError
            ? "The group was not saved. Check your access and try again."
            : null;
  const valid =
    fields.name.trim().length > 0 &&
    (fields.meetingTime === "" || /^([01]\d|2[0-3]):[0-5]\d$/.test(fields.meetingTime)) &&
    (fields.ageMin === null || fields.ageMax === null || fields.ageMax >= fields.ageMin);

  return (
    <>
      <Modal
        opened={group !== undefined}
        onClose={onClose}
        title={group === null ? "Add a group" : "Edit group"}
        radius="lg"
        size="lg"
      >
        <Stack gap="sm">
          <TextInput
            label="Name"
            required
            disabled={!editable}
            value={fields.name}
            onChange={text("name")}
          />
          <Textarea
            label="Description"
            description="What a member reads before joining, and what the website shows for a public group."
            disabled={!editable}
            value={fields.description}
            onChange={text("description")}
            autosize
            minRows={2}
            maxRows={6}
          />
          <Group grow align="flex-start">
            <Select
              label="Category"
              placeholder="None"
              clearable
              disabled={!editable}
              data={(categories.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
              value={fields.categoryId}
              onChange={(value) => {
                setFields((current) => ({ ...current, categoryId: value }));
              }}
            />
            <Select
              label="Campus"
              placeholder="None"
              clearable
              disabled={!editable}
              data={campuses.data ?? []}
              value={fields.campusId}
              onChange={(value) => {
                setFields((current) => ({ ...current, campusId: value }));
              }}
            />
          </Group>
          <Group grow align="flex-start">
            <Select
              label="Meets on"
              placeholder="Not set"
              clearable
              disabled={!editable}
              data={DAYS}
              value={fields.meetingDay}
              onChange={(value) => {
                const day = DAYS.find((d) => d.value === value)?.value ?? null;
                setFields((current) => ({ ...current, meetingDay: day }));
              }}
            />
            <TextInput
              label="At"
              type="time"
              disabled={!editable}
              value={fields.meetingTime}
              onChange={text("meetingTime")}
            />
            <TextInput
              label="Where"
              placeholder="Room 4, or a home"
              disabled={!editable}
              value={fields.meetingLocation}
              onChange={text("meetingLocation")}
            />
          </Group>
          <Group grow align="flex-start">
            <Select
              label="Who can see it"
              description="Public is on the website and in the portal; unlisted needs the link; private is its members and staff."
              disabled={!editable}
              data={VISIBILITIES}
              value={fields.visibility}
              allowDeselect={false}
              onChange={(value) => {
                const v = VISIBILITIES.find((o) => o.value === value)?.value;
                if (v !== undefined) setFields((current) => ({ ...current, visibility: v }));
              }}
            />
            <Select
              label="How people join"
              description="Open joins at once; by request waits for a leader; closed means staff add people."
              disabled={!editable}
              data={JOINING}
              value={fields.joiningRule}
              allowDeselect={false}
              onChange={(value) => {
                const v = JOINING.find((o) => o.value === value)?.value;
                if (v !== undefined) setFields((current) => ({ ...current, joiningRule: v }));
              }}
            />
          </Group>
          <Group grow align="flex-start">
            <NumberInput
              label="Capacity"
              description="Members, leaders excluded. Only a member's own join is refused at capacity."
              placeholder="No limit"
              disabled={!editable}
              min={1}
              max={10000}
              value={fields.capacity ?? ""}
              onChange={number("capacity")}
            />
            <NumberInput
              label="Youngest age"
              placeholder="Any"
              disabled={!editable}
              min={0}
              max={120}
              value={fields.ageMin ?? ""}
              onChange={number("ageMin")}
            />
            <NumberInput
              label="Oldest age"
              placeholder="Any"
              disabled={!editable}
              min={0}
              max={120}
              value={fields.ageMax ?? ""}
              onChange={number("ageMax")}
            />
          </Group>
          <Checkbox
            label="Members are confidential"
            description="Members see only the leaders. Otherwise members of the group see each other by name, adults only."
            disabled={!editable}
            checked={fields.membersConfidential}
            onChange={(e) => {
              const membersConfidential = e.currentTarget.checked;
              setFields((current) => ({ ...current, membersConfidential }));
            }}
          />
          {group !== null && group !== undefined ? (
            <TextInput
              label="Address"
              description="The group's address on the website and in links. Changing it breaks links people have."
              disabled={!editable}
              value={fields.slug}
              onChange={text("slug")}
            />
          ) : null}
          {errorText !== null ? (
            <Text size="sm" c="red">
              {errorText}
            </Text>
          ) : null}
          <Group justify="space-between" mt="xs">
            <Group gap="xs">
              {group !== null && group !== undefined && canManage ? (
                group.isActive ? (
                  <Button
                    variant="subtle"
                    color="red"
                    onClick={() => {
                      setConfirming(true);
                    }}
                  >
                    Archive
                  </Button>
                ) : (
                  <Button
                    variant="subtle"
                    loading={setActive.isPending}
                    onClick={() => {
                      setActive.mutate(
                        { groupId: group.id, isActive: true },
                        { onSuccess: onClose },
                      );
                    }}
                  >
                    Restore
                  </Button>
                )
              ) : null}
            </Group>
            {editable ? (
              <Group gap="sm">
                <Button variant="subtle" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  disabled={!valid}
                  loading={save.isPending}
                  onClick={() => {
                    save.mutate(
                      { groupId: group === null || group === undefined ? null : group.id, fields },
                      {
                        onSuccess: (id) => {
                          if (group === null && onCreated !== undefined) onCreated(id);
                          else onClose();
                        },
                      },
                    );
                  }}
                >
                  {group === null ? "Add group" : "Save group"}
                </Button>
              </Group>
            ) : (
              <Button variant="subtle" onClick={onClose}>
                Close
              </Button>
            )}
          </Group>
        </Stack>
      </Modal>
      <ConfirmActionModal
        opened={confirming}
        onClose={() => {
          setConfirming(false);
        }}
        title="Archive this group"
        message={
          group === null || group === undefined
            ? ""
            : `Archive ${group.name}? It keeps its seasons and rosters and stops appearing to members. You can restore it later.`
        }
        confirmLabel="Archive"
        danger
        loading={setActive.isPending}
        onConfirm={() => {
          if (group === null || group === undefined) return;
          setActive.mutate(
            { groupId: group.id, isActive: false },
            {
              onSuccess: () => {
                setConfirming(false);
                onClose();
              },
            },
          );
        }}
      />
    </>
  );
}
